/**
 * forecast-chart-plugin.js
 *
 * Chart.js inline plugin for the forecast chart.
 *
 * chart._forecastHasForecast  {boolean|undefined}  set by charts.js after fetch
 */

import Chart from 'chart.js/auto';

const isForecastCanvas = (chart) =>
  chart.canvas?.getAttribute('data-chart')?.includes('forecast-chart.php') ?? false;

// ---------------------------------------------------------------------------
// Callbacks installed after init.
// ---------------------------------------------------------------------------

const forecastTooltipTitle = (items) => {
  if (!items.length) return '';
  const dt = new Date(items[0].parsed.x);
  return dt.toLocaleString('de-CH', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const forecastTooltipLabel = (item) => {
  if (item.parsed.y === null) return null;
  return ` ${item.dataset.label}: ${item.parsed.y.toLocaleString('de-CH')} kWh`;
};

const forecastGridColor = (ctx) => {
  const val = ctx.tick?.value ?? 0;
  const dt = new Date(val);
  return dt.getHours() === 0
    ? 'rgba(255, 255, 255, 0.22)'
    : 'rgba(255, 255, 255, 0.08)';
};

const forecastTickCallback = function (value, index, ticks) {
  const dt = new Date(ticks[index]?.value ?? value);
  if (dt.getHours() === 0) {
    return dt.toLocaleDateString('de-CH', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    });
  }
  if (dt.getHours() % 6 === 0) {
    return dt.toLocaleTimeString('de-CH', {hour: '2-digit', minute: '2-digit'});
  }
  return '';
};

// ---------------------------------------------------------------------------
// Exported helper — called by charts.js after each fetch.
// Sets forecast dataset visibility directly, no afterRender loop needed.
// ---------------------------------------------------------------------------

export function applyForecastVisibility (chart, hasForecast) {
  if (!isForecastCanvas(chart)) return;

  chart._forecastHasForecast = hasForecast;

  // Only force-show when hasForecast changes to true and user has not
  // explicitly hidden it. Never force-show if user hid it intentionally.
  const userHidden = chart._forecastUserHidden ?? false;
  const meta = chart.getDatasetMeta(1);
  if (!meta) return;

  meta.hidden = !hasForecast || userHidden;
  chart.update('none');
}

// ---------------------------------------------------------------------------
// Plugin
// ---------------------------------------------------------------------------

const ForecastChartPlugin = {
  id: 'forecastChartPlugin',

  afterInit (chart) {
    if (!isForecastCanvas(chart)) return;

    chart.options.plugins.tooltip.callbacks.title = forecastTooltipTitle;
    chart.options.plugins.tooltip.callbacks.label = forecastTooltipLabel;
    chart.options.scales.x.grid.color = forecastGridColor;
    chart.options.scales.x.ticks.callback = forecastTickCallback;
    chart.options.scales.x.ticks.major = {enabled: true};

    // Override legend onClick to track user intent.
    const defaultOnClick = Chart.defaults.plugins.legend.onClick;
    chart.options.plugins.legend.onClick = function (e, legendItem, legend) {
      defaultOnClick.call(this, e, legendItem, legend);

      if (legendItem.datasetIndex === 1) {
        const meta = chart.getDatasetMeta(1);
        if (meta) {
          chart._forecastUserHidden = meta.hidden;
        }
      }
    };
  },
  // No afterRender — visibility is managed explicitly via applyForecastVisibility().
};

Chart.register(ForecastChartPlugin);
